import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';
export type AppTheme = 'dark' | 'light' | 'oled' | 'catppuccin-mocha' | 'dracula' | 'nord' | 'tokyo-night' | 'gruvbox' | 'rose-pine' | 'solarized-dark';
export type AccentPalette = 'blue' | 'purple' | 'green' | 'rose' | 'amber' | 'nord' | 'dracula';
export type Density = 'comfortable' | 'compact' | 'ultra-compact';

/** The 10 curated app themes */
export const APP_THEMES: { id: AppTheme; label: string; swatches: string[] }[] = [
  { id: 'dark',              label: 'Dark',              swatches: ['#0f172a', '#3b82f6', '#3b82f6'] },
  { id: 'light',             label: 'Light',             swatches: ['#f8fafc', '#3b82f6', '#3b82f6'] },
  { id: 'oled',              label: 'OLED',              swatches: ['#000000', '#3b82f6', '#3b82f6'] },
  { id: 'catppuccin-mocha',  label: 'Catppuccin Mocha',  swatches: ['#1e1e2e', '#89b4fa', '#cba6f7'] },
  { id: 'dracula',           label: 'Dracula',           swatches: ['#282a36', '#bd93f9', '#ff79c6'] },
  { id: 'nord',              label: 'Nord',              swatches: ['#2e3440', '#88c0d0', '#81a1c1'] },
  { id: 'tokyo-night',       label: 'Tokyo Night',       swatches: ['#1a1b26', '#7aa2f7', '#bb9af7'] },
  { id: 'gruvbox',           label: 'Gruvbox',           swatches: ['#282828', '#d79921', '#98971a'] },
  { id: 'rose-pine',         label: 'Rosé Pine',         swatches: ['#191724', '#c4a7e7', '#ebbcba'] },
  { id: 'solarized-dark',    label: 'Solarized Dark',    swatches: ['#002b36', '#268bd2', '#2aa198'] },
];

/** Paletas de accent — cada uma define HSL do primary + glow (override das
 *  vars do CSS). Os valores restantes (background, fg, etc) ficam iguais. */
const PALETTES: Record<AccentPalette, { light: [string, string]; dark: [string, string] }> = {
  blue: { light: ['217 91% 55%', '217 91% 70%'], dark: ['217 91% 62%', '217 91% 75%'] },
  purple: { light: ['262 83% 58%', '262 83% 73%'], dark: ['262 83% 65%', '262 83% 78%'] },
  green: { light: ['160 84% 39%', '160 84% 54%'], dark: ['160 84% 48%', '160 84% 63%'] },
  rose: { light: ['346 77% 50%', '346 77% 65%'], dark: ['346 77% 58%', '346 77% 73%'] },
  amber: { light: ['43 96% 48%', '43 96% 63%'], dark: ['43 96% 55%', '43 96% 70%'] },
  nord: { light: ['207 44% 49%', '207 44% 64%'], dark: ['207 44% 58%', '207 44% 73%'] },
  dracula: { light: ['265 89% 66%', '265 89% 78%'], dark: ['265 89% 72%', '265 89% 82%'] },
};

function apply(theme: Theme, palette: AccentPalette, density: Density, bgImage?: string | null, appTheme?: AppTheme) {
  const dark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);

  // App theme (data-theme attribute for CSS variable sets)
  if (appTheme) {
    document.documentElement.dataset.theme = appTheme;
    // For dark themes: force dark class for Tailwind dark: variants
    const darkThemes: AppTheme[] = ['oled', 'catppuccin-mocha', 'dracula', 'nord', 'tokyo-night', 'gruvbox', 'rose-pine', 'solarized-dark'];
    if (darkThemes.includes(appTheme)) {
      document.documentElement.classList.add('dark');
    } else if (appTheme === 'light') {
      document.documentElement.classList.remove('dark');
    }
  } else {
    delete document.documentElement.dataset.theme;
  }

  // Accent palette — sobrepõe primary + primary-glow (only when no custom app theme)
  if (!appTheme || appTheme === 'dark' || appTheme === 'light') {
    const [p, pg] = dark ? PALETTES[palette].dark : PALETTES[palette].light;
    document.documentElement.style.setProperty('--primary', p);
    document.documentElement.style.setProperty('--primary-glow', pg);
  } else {
    // Reset inline overrides so CSS variables from data-theme take effect
    document.documentElement.style.removeProperty('--primary');
    document.documentElement.style.removeProperty('--primary-glow');
  }

  // Density — aplicado via data-attribute para CSS hook
  document.documentElement.dataset.density = density;

  // Background image custom
  if (bgImage) {
    document.body.style.backgroundImage = `url(${JSON.stringify(bgImage)})`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.dataset.customBg = 'true';
  } else {
    document.body.style.backgroundImage = '';
    document.body.style.backgroundSize = '';
    document.body.style.backgroundAttachment = '';
    delete document.body.dataset.customBg;
  }
}

interface ThemeState {
  theme: Theme;
  appTheme: AppTheme | null;
  palette: AccentPalette;
  density: Density;
  bgImage: string | null;
  setTheme: (t: Theme) => void;
  setAppTheme: (t: AppTheme | null) => void;
  setPalette: (p: AccentPalette) => void;
  setDensity: (d: Density) => void;
  setBgImage: (url: string | null) => void;
  init: () => void;
}

export const useTheme = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      appTheme: null,
      palette: 'blue',
      density: 'comfortable',
      bgImage: null,
      setTheme(t) {
        set({ theme: t });
        apply(t, get().palette, get().density, get().bgImage, get().appTheme ?? undefined);
      },
      setAppTheme(t) {
        set({ appTheme: t });
        // Sync localStorage key 'theme' for legacy consumers
        try { if (t) localStorage.setItem('theme', t); else localStorage.removeItem('theme'); } catch { /* ignore */ }
        const baseTheme = t === 'light' ? 'light' : t ? 'dark' : get().theme;
        apply(baseTheme, get().palette, get().density, get().bgImage, t ?? undefined);
      },
      setPalette(p) {
        set({ palette: p });
        apply(get().theme, p, get().density, get().bgImage, get().appTheme ?? undefined);
      },
      setDensity(d) {
        set({ density: d });
        apply(get().theme, get().palette, d, get().bgImage, get().appTheme ?? undefined);
      },
      setBgImage(url) {
        set({ bgImage: url });
        apply(get().theme, get().palette, get().density, url, get().appTheme ?? undefined);
      },
      init() {
        const s = get();
        // Apply saved app theme on load
        const baseTheme = s.appTheme === 'light' ? 'light' : s.appTheme ? 'dark' : s.theme;
        apply(baseTheme, s.palette, s.density, s.bgImage, s.appTheme ?? undefined);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
          const cur = get();
          if (!cur.appTheme && cur.theme === 'system') apply('system', cur.palette, cur.density, cur.bgImage);
        });
      },
    }),
    {
      name: 'theme-v2',
      // Migration from theme-v1 (string only) — pnpm persist handles missing
      partialize: (s) => ({ theme: s.theme, appTheme: s.appTheme, palette: s.palette, density: s.density, bgImage: s.bgImage }),
    },
  ),
);

export const PALETTE_LABELS: Record<AccentPalette, string> = {
  blue: 'Azul (default)',
  purple: 'Púrpura',
  green: 'Verde',
  rose: 'Rosa',
  amber: 'Âmbar',
  nord: 'Nord',
  dracula: 'Dracula',
};
